import {
  AnimationCallbackEvent,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { MediaViewDialogData } from '../models/media-view-dialog-data.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  getImagePreview,
  MEDIA_VIEWER_UNZOOMED_SIZES,
} from '../media-view.service';
import {
  getAttachmentDocumentSource,
  getAttachmentRepr,
  isAttachmentPreviewPending,
  isAudioAttachment,
} from '@fc-core/files-uploads/utils/attachment-repr.utils';
import { MediaViewPlaceholderComponent } from '../media-view-placeholder/media-view-placeholder.component';
import { buildThumbnailSrcset } from '@fc-shared/ui/image-loader/thumbnail-tiers';
import { MediaViewPdfComponent } from '../media-view-pdf/media-view-pdf.component';
import { PdfZoomControlsComponent } from '../media-view-pdf/pdf-zoom-controls.component';
import { MediaViewVideoComponent } from '../media-view-video/media-view-video.component';
import { MediaViewPhotoComponent } from '../media-view-photo/media-view-photo.component';
import { MediaViewAudioComponent } from '../media-view-audio/media-view-audio.component';
import { MediaViewerHeaderComponent } from './media-viewer.header';
import { MediaViewerDetailsComponent } from './media-viewer.details';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { MediaControlsComponent } from './media-controls';
import { ArrowNavigationDirective } from '../directives/arrow-navigation.directive';
import { AttachmentModel } from '@fc-vehicles/service/attachment-model';
import { isMobile } from '@fc-shared/utils/is-mobile';
import { ModalBottomSheetService } from '@fc-shared/ui/modal-bottom-sheet/modal-bottom-sheet.service';
import {
  MediaViewerBottomsheetComponent,
  MediaViewerSheetData,
} from './media-viewer.bottomsheet';
import { ConfirmDialogData } from '@fc-shared/ui/confirm-dialog/confirm-dialog.data';
import { DialogsService } from '@fc-core/services';
import { take } from 'rxjs';

export const MEDIA_VIEWER_DETAILS_PANEL_WIDTH = 300;

@Component({
  selector: 'fc-media-viewer-dialog',
  template: `
    <div
      fcArrowNavigation
      [fcArrowNavigationNext]="handleNext"
      [fcArrowNavigationPrev]="handlePrev"
      class="flex h-full flex-col"
    >
      <fc-media-viewer-header
        [canDelete]="data.deleteFunction !== undefined"
        [file]="currentFile()"
        (openInfo)="toggleMediaInfo()"
        (closeDialog)="closeDialog()"
        (deleteFile)="onDeleteFile(currentFile())"
      >
        @if (pdfViewer(); as pdfViewer) {
          <fc-pdf-zoom-controls class="max-md:hidden" [viewer]="pdfViewer" />
        }
      </fc-media-viewer-header>
      <div class="relative flex flex-1 overflow-hidden">
        <div
          class="media-shift flex h-full min-w-0 items-center justify-center relative"
          [class.media-shift-open]="mediaInfoOpen()"
        >
          @if (data?.files?.length > 1) {
            <div
              (click)="nextFile()"
              class="z-10 flex items-center justify-center size-48 rounded-full bg-black/50 absolute right-0 sm:right-30 cursor-pointer"
            >
              <fc-icon
                color="white"
                class="size-24"
                icon="icon-arrow-right"
              ></fc-icon>
            </div>
            <div
              (click)="prevFile()"
              class="z-10 flex items-center justify-center size-48 rounded-full bg-black/50 absolute left-0 sm:left-30 cursor-pointer"
            >
              <fc-icon
                color="white"
                class="size-24"
                icon="icon-arrow-left"
              ></fc-icon>
            </div>
          }
          <div
            class="relative flex h-full w-full items-center justify-center overflow-hidden"
          >
            @for (entry of currentEntryList(); track entry.key) {
              <div
                class="flex h-full w-full items-center justify-center"
                (animate.enter)="animateMediaSlideEnter($event)"
                (animate.leave)="animateMediaSlideLeave($event)"
              >
                @switch (entry.viewer) {
                  @case ('pdf') {
                    <!-- Lazy-load PDF viewer to avoid initializing the EmbedPDF runtime during tests -->
                    <div class="size-full md:px-30 md:pb-30">
                      @defer {
                        <fc-media-view-pdf
                          #pdfViewer
                          class="md:rounded-lg"
                          [file]="{ original: entry.pdfSource }"
                          (fileLoaded)="isFileLoaded.set($event)"
                        ></fc-media-view-pdf>
                      }
                    </div>
                  }
                  @case ('video') {
                    <fc-media-view-video
                      [file]="entry.file"
                      (fileLoaded)="isFileLoaded.set($event)"
                    ></fc-media-view-video>
                  }
                  @case ('audio') {
                    <fc-media-view-audio
                      [fileName]="entry.file.name ?? 'Audio'"
                      [source]="entry.downloadUrl"
                      [waveform]="entry.file.waveform"
                      [durationSeconds]="entry.file.duration"
                    />
                  }
                  @case ('photo') {
                    <fc-media-view-photo
                      [file]="entry.file"
                      [isFileLoaded]="isFileLoaded()"
                      (fileLoaded)="isFileLoaded.set($event)"
                    ></fc-media-view-photo>
                  }
                  @default {
                    <fc-media-view-placeholder
                      [fileName]="entry.file.name ?? 'File'"
                      [downloadUrl]="entry.downloadUrl"
                      [isPreviewPending]="entry.isPreviewPending"
                    />
                  }
                }
              </div>
            }
          </div>

          <fc-media-controls
            [files]="data.files"
            [currentIndex]="currentIndex()"
          ></fc-media-controls>
        </div>
        @if (mediaInfoOpen()) {
          <fc-media-viewer-details
            class="absolute inset-y-0 right-0 bg-white shadow-lg"
            [style.width.px]="detailsPanelWidth"
            animate.enter="fc-anim-slide-in-right"
            animate.leave="fc-anim-slide-out-right"
            [file]="currentFile()"
            (closeInfo)="mediaInfoOpen.set(false)"
          ></fc-media-viewer-details>
        }
      </div>
    </div>
  `,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MediaViewPdfComponent,
    PdfZoomControlsComponent,
    MediaViewVideoComponent,
    MediaViewPhotoComponent,
    MediaViewAudioComponent,
    MediaViewPlaceholderComponent,
    MediaViewerHeaderComponent,
    MediaViewerDetailsComponent,
    IconComponent,
    MediaControlsComponent,
    ArrowNavigationDirective,
  ],
  styles: `
    .media-shift {
      width: 100%;
      transition: width 280ms cubic-bezier(0.4, 0, 0.2, 1);
    }

    /* Details panel width plus its 30px right margin, so content keeps the
       same 30px gap from the panel as the panel keeps from the edge. */
    .media-shift-open {
      width: calc(100% - 330px);
      min-width: 300px;
    }

    .media-slide-leaving {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }
  `,
})
export class MediaViewerDialogComponent {
  data = inject<MediaViewDialogData>(MAT_DIALOG_DATA);
  isFileLoaded = signal<boolean>(false);
  protected readonly pdfViewer = viewChild<MediaViewPdfComponent>('pdfViewer');
  mediaInfoOpen = signal<boolean>(false);
  detailsPanelWidth = MEDIA_VIEWER_DETAILS_PANEL_WIDTH;
  dialogRef = inject(MatDialogRef<MediaViewerDialogComponent>);
  currentIndex = signal<number>(this.data.currentIndex);
  currentFile = computed(() => this.data.files[this.currentIndex()]);
  dialogsService = inject(DialogsService);
  readonly currentEntry = computed(() => {
    const file = this.currentFile();
    if (!file) return null;
    const pdfSource = getAttachmentDocumentSource(file);
    const viewer = this.resolveViewer(file, pdfSource);
    const downloadUrl =
      typeof file.original === 'string' ? file.original : null;
    const isPreviewPending = isAttachmentPreviewPending(file);
    const key =
      (file as any)?.id ??
      (file as any)?.mediaId ??
      (file as any)?.guid ??
      (file as any)?.uuid ??
      `${viewer}-${this.currentIndex()}`;
    return { file, viewer, pdfSource, downloadUrl, isPreviewPending, key };
  });
  readonly currentEntryList = computed(() =>
    this.currentEntry() ? [this.currentEntry()] : [],
  );
  private readonly bottomSheet = inject(ModalBottomSheetService);
  private readonly preloadedImageUrls = new Set<string>();
  private readonly slideDirection = signal<'init' | 'forward' | 'backward'>(
    'init',
  );
  private readonly _preloadEffect = effect(() => {
    if (!this.data?.files?.length) return;
    const entry = this.currentEntry();
    if (!entry) return;
    this.preloadPhoto(entry.file);
    this.preloadSurroundingPhotos(this.currentIndex());
  });

  readonly handleNext = () => this.nextFile();

  readonly handlePrev = () => this.prevFile();

  toggleMediaInfo(): void {
    if (isMobile()) {
      const data: MediaViewerSheetData = { file: this.currentFile() };
      this.bottomSheet.open(MediaViewerBottomsheetComponent, { data });
      return;
    }
    this.mediaInfoOpen.update((isOpen) => !isOpen);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  nextFile(): void {
    if (!this.canNavigate()) return;
    const newIndex = this.currentIndex() + 1;
    this.changeFileIndex(
      newIndex >= this.data.files.length ? 0 : newIndex,
      'forward',
    );
  }

  prevFile(): void {
    if (!this.canNavigate()) return;
    const newIndex = this.currentIndex() - 1;
    this.changeFileIndex(
      newIndex < 0 ? this.data.files.length - 1 : newIndex,
      'backward',
    );
  }

  onDeleteFile(media: AttachmentModel) {
    const confirmData: ConfirmDialogData = {
      title: `Deleting file?`,
      message: 'Are you sure, you want to delete this file? ',
      buttonText: 'DELETE',
      action: () => this.data.deleteFunction?.(media),
      buttonColor: 'warn',
    };

    this.dialogsService
      .openConfirmDialog(confirmData)
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => this.dialogRef.close());
  }

  private resolveViewer(
    file: AttachmentModel,
    pdfSource: string | null,
  ): 'pdf' | 'video' | 'audio' | 'photo' | 'placeholder' {
    if (pdfSource) return 'pdf';
    if (isAudioAttachment(file)) return 'audio';
    switch (getAttachmentRepr(file).type) {
      case 'video':
        return 'video';
      case 'image':
        return 'photo';
      default:
        return 'placeholder';
    }
  }

  private canNavigate(): boolean {
    return Array.isArray(this.data?.files) && this.data.files.length > 1;
  }

  private changeFileIndex(
    normalizedIndex: number,
    direction: 'forward' | 'backward',
  ): void {
    if (normalizedIndex === this.currentIndex()) return;
    this.triggerSlide(direction);
    this.isFileLoaded.set(false);
    this.currentIndex.set(normalizedIndex);
  }

  private triggerSlide(direction: 'forward' | 'backward'): void {
    if (!this.canNavigate()) return;
    this.slideDirection.set(direction);
  }

  animateMediaSlideEnter(event: AnimationCallbackEvent): void {
    const direction = this.slideDirection();
    if (direction === 'init') {
      event.animationComplete();
      return;
    }
    const enterShift = direction === 'forward' ? '30%' : '-30%';
    const slideIn = (event.target as HTMLElement).animate(
      {
        transform: [`translateX(${enterShift})`, 'translateX(0)'],
        opacity: [0, 1],
      },
      { duration: 260, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
    );
    slideIn.finished.then(
      () => event.animationComplete(),
      () => event.animationComplete(),
    );
  }

  animateMediaSlideLeave(event: AnimationCallbackEvent): void {
    const direction = this.slideDirection();
    if (direction === 'init') {
      event.animationComplete();
      return;
    }
    const target = event.target as HTMLElement;
    target.classList.add('media-slide-leaving');
    const exitShift = direction === 'forward' ? '-30%' : '30%';
    const slideOut = target.animate(
      {
        transform: ['translateX(0)', `translateX(${exitShift})`],
        opacity: [1, 0],
      },
      {
        duration: 260,
        easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
        fill: 'forwards',
      },
    );
    slideOut.finished.then(
      () => event.animationComplete(),
      () => event.animationComplete(),
    );
  }

  private preloadSurroundingPhotos(currentIndex: number): void {
    if (!this.canPreload()) return;
    const total = this.data.files.length;
    const nextIndex = (currentIndex + 1) % total;
    const prevIndex = (currentIndex - 1 + total) % total;
    this.preloadPhoto(this.data.files[nextIndex]);
    this.preloadPhoto(this.data.files[prevIndex]);
  }

  private preloadPhoto(file: AttachmentModel | undefined): void {
    if (!file || getAttachmentRepr(file).type !== 'image') return;
    this.preloadImage(file);
  }

  private preloadImage(file: AttachmentModel): void {
    const url = getImagePreview(file);
    if (
      !url ||
      this.preloadedImageUrls.has(url) ||
      typeof window === 'undefined'
    ) {
      return;
    }
    const image = new Image();
    image.sizes = MEDIA_VIEWER_UNZOOMED_SIZES;
    image.srcset = buildThumbnailSrcset(file.thumbnails).join(', ');
    image.src = url;
    this.preloadedImageUrls.add(url);
  }

  private canPreload(): boolean {
    return Array.isArray(this.data?.files) && this.data.files.length > 0;
  }
}
